<template>
  <div v-if="isLoading" class="d-flex justify-center align-center">
    <v-row no-gutters class="px-12 py-6">
      <v-col cols="12" class="d-flex justify-center align-center">
        <v-progress-circular
          indeterminate
          color="#2661FF"
          size="80"
        ></v-progress-circular>
      </v-col>
    </v-row>
  </div>
  <div v-else>
    <!-- ORGANIZATION INFO -->
    <v-row no-gutters class="px-12 py-6">
      <v-col cols="12">
        <v-card flat class="rounded-lg">
          <v-card-title class="px-10 py-5">
            {{ organization.name }}
            <v-spacer />
            <v-chip
              class="ma-2"
              :color="getStatusObject(organization.status).color"
              outlined
            >
              <v-icon>
                {{ getStatusObject(organization.status).icon }}
              </v-icon>
              <span class="ml-1">
                {{ getStatusObject(organization.status).text }}
              </span>
            </v-chip>
          </v-card-title>
          <v-divider />
          <!-- DO NOT SHOW BUTTONS IF THE ORGANIZATION IS [ CANCELED - SUSPENDED - DELETED] OR IF IT HAS NO PLANS -->
          <v-card-text v-if="showOrganizationHeaderActions">
            <v-row no-gutters>
              <v-col cols="6" class="button-container">
                <v-btn
                  v-if="organization.organization_plan"
                  small
                  text
                  color="#2661FF"
                  @click="openBillingDrawer = !openBillingDrawer"
                >
                  <v-icon>description</v-icon>
                  <span class="ml-1 font-weight-bold">{{
                    $t('organization.details.billing')
                  }}</span>
                </v-btn>

                <v-btn
                  v-if="organization.organization_plan"
                  small
                  text
                  color="#2661FF"
                  @click="showHistoric = !showHistoric"
                >
                  <v-icon>schedule</v-icon>
                  <span class="ml-1 font-weight-bold">{{
                    $t('organization.details.billing_history')
                  }}</span>
                </v-btn>

                <v-btn
                  v-if="organization.organization_plan"
                  small
                  text
                  color="#2661FF"
                  @click="openAccountDetails = !openAccountDetails"
                >
                  <v-icon>info</v-icon>
                  <span class="ml-1 font-weight-bold">{{
                    $t('organization.details.account_details')
                  }}</span>
                </v-btn>
              </v-col>
              <v-col cols="6" class="d-flex justify-end">
                <v-btn
                  small
                  text
                  color="#2661FF"
                  @click="openObservationsDialog"
                >
                  <v-icon>description</v-icon>
                  <span class="font-weight-bold">{{
                    $t('organization.details.observations')
                  }}</span>
                </v-btn>
                <v-btn small text color="#2661FF" @click="openQuickbooksDialog">
                  <v-icon>receipt_long</v-icon>
                  <span class="font-weight-bold">Quickbooks</span>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider />
          <v-card-text>
            <v-row>
              <v-col cols="12" md="7">
                <v-row class="ml-4">
                  <v-col cols="12">
                    <p class="subtitle-1 font-weight-medium black--text">
                      {{ $t('organization.details.account_details') }}
                    </p>
                  </v-col>
                  <v-col cols="12">
                    <!-- CREATED AT -->
                    <InfoRow
                      :label="$t('general.created_at')"
                      :value="
                        $moment(organization.created_at).format('DD/MM/YYYY')
                      "
                    />
                    <!-- LINK -->
                    <InfoRow
                      :label="$t('articles.table.link')"
                      :value="organization.url ? organization.url : '-'"
                    />
                    <!-- COUNTRY -->
                    <InfoRow
                      :label="$t('profile.profile.country')"
                      :value="organization.country.name"
                    />
                    <!-- CURRENCY -->
                    <InfoRow
                      :label="$t('organization.org_currency')"
                      :value="organization.currency.name"
                    />
                    <!-- DELETED_AT -->
                    <InfoRow
                      v-if="isOrganizationDeleted()"
                      :label="`${getStatusObject(organization.status).text} el`"
                      :value="
                        $moment(organization.deleted_at).format('DD/MM/YYYY')
                      "
                    />
                    <!-- PLAN CURRENCY -->
                    <!-- IF ORGANIZATION IS [DELETED - SUSPENDED - CANCELED] IT WONT BE ABLE TO SELECT A PLAN -->
                    <InfoRow
                      v-if="
                        !filterOrganizationStatus.includes(
                          getStatusObject(organization.status).value
                        ) &&
                          organization.organization_plan &&
                          ![getPlanIdByName('no_plan')].includes(
                            organization.organization_plan.plan_id
                          )
                      "
                      :label="$t('organization_billing.plan_currency')"
                      :value="
                        organization.organization_plan &&
                        organization.organization_plan.currency
                          ? `${organization.organization_plan.currency.name} (${organization.organization_plan.currency.code})`
                          : '-'
                      "
                    />
                    <!-- PLAN -->
                    <!-- IF ORGANIZATION IS [DELETED - SUSPENDED - CANCELED] IT WONT BE ABLE TO SELECT A PLAN -->
                    <InfoRow
                      v-if="
                        !filterOrganizationStatus.includes(
                          getStatusObject(organization.status).value
                        )
                      "
                      :label="$t('organization_billing.plan')"
                      :value="
                        organization.organization_plan &&
                        organization.organization_plan.plan.display_name
                          ? organization.organization_plan.plan.display_name
                          : '-'
                      "
                      :modifyPlan="
                        organization.organization_plan &&
                        organization.organization_plan.plan.name
                          ? true
                          : false
                      "
                      :isPlan="true"
                      @open-plan="openPlan(organization.id)"
                      :disabled="isOrganizationPlanDetailsOpen"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="5" class="pr-8 pt-12">
                <v-card color="#2661FF" class="white--text rounded-xl">
                  <v-card-title
                    class="px-12 subtitle-1 font-weight-medium pt-6"
                  >
                    <v-icon color="white">timeline</v-icon>
                    <span class="ml-4">{{
                      $t('organization.details.general_numbers')
                    }}</span>
                  </v-card-title>
                  <v-card-text class="white--text">
                    <v-row no-gutters>
                      <v-col
                        cols="6"
                        class="d-flex flex-column justify-center align-center"
                      >
                        <p class="body-1 font-weight-medium">
                          {{ $t('organization.details.tabs.users.title') }}
                        </p>
                        <p class="text-h5 font-weight-black">
                          {{
                            organization.users | valueFormat(organization.users)
                          }}
                        </p>
                      </v-col>
                      <v-col
                        cols="6"
                        class="d-flex flex-column justify-center align-center"
                      >
                        <p class="body-1 font-weight-medium">
                          {{ $t('organization.details.processed_donations') }}
                        </p>
                        <p class="text-h5 font-weight-black">
                          {{
                            organization.donations
                              | valueFormat(organization.donations)
                          }}
                        </p>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- USER INFO -->
    <v-row no-gutters class="px-12 py-6">
      <v-col cols="12">
        <v-card flat class="rounded-lg">
          <v-card-text>
            <v-row>
              <v-col cols="12" md="7">
                <v-row class="ml-4">
                  <v-col cols="12">
                    <p class="subtitle-1 font-weight-medium black--text pt-4">
                      {{ $t('organization.details.client_info') }}
                    </p>
                  </v-col>
                  <!-- NAME -->
                  <v-col cols="12">
                    <InfoRow
                      :label="$t('organization.details.tabs.users.name')"
                      :value="organization.contact_name"
                    />
                    <!-- PHONE -->
                    <InfoRow
                      :label="$t('organization.details.tabs.users.phone')"
                      :value="organization.phone"
                    />
                    <!-- CONTACT -->
                    <InfoRow
                      :label="$t('organization.org_email')"
                      :value="organization.contact_info"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- DASHBOARD -->
    <v-row no-gutters class="px-12 py-6">
      <v-col cols="12">
        <v-card class="rounded-lg" flat>
          <v-card-title
            class="subtitle-1 font-weight-medium black--text pt-4 ml-6"
          >
            {{ $t('organization.details.tabs.dashboard.title') }}
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" class="d-flex justify-end">
                <DateBar
                  ref="date-bar"
                  :dateRange="dashboard.dateRange"
                  @updateRange="
                    (value, range, index) => {
                      dashboard.dateRange = index
                      dashboard.endDate = $moment().format('YYYY-MM-DD')
                      changeDates(value, range)
                    }
                  "
                />
                <DateFilters
                  ref="date-filters"
                  :initialDate="dashboard.startDate"
                  :finalDate="dashboard.endDate"
                  @updateInitialDate="updateDates('startDate', $event)"
                  @updateFinalDate="updateDates('endDate', $event)"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <LineChart
                  :height="$vuetify.breakpoint.mdAndUp ? 130 : 200"
                  :chart-data="dashboard.datacollection"
                  :options="{
                    scales: {
                      yAxes: [
                        {
                          id: 'totalLeads',
                          type: 'linear',
                          position: 'left',
                          ticks: {
                            precision: 0,
                            beginAtZero: true
                          }
                        },
                        {
                          id: 'totalDonations',
                          type: 'linear',
                          position: 'right',
                          ticks: {
                            callback: function(label, index, labels) {
                              return `${currency} ${label}`
                            },
                            beginAtZero: true
                          }
                        }
                      ]
                    }
                  }"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- USERS -->
    <v-row no-gutters class="px-12 py-6">
      <v-col cols="12">
        <v-card class="rounded-lg" flat>
          <v-card-title
            class="subtitle-1 font-weight-medium black--text pt-4 ml-6"
          >
            {{ $t('organization.details.tabs.users.title') }}
            <v-spacer />
            <v-btn class="rounded-lg" outlined color="#2661FF" @click="create"
              ><v-icon>add</v-icon>
              <span class="mr-1">
                {{ $t('organization.users.new') }}
              </span></v-btn
            >
          </v-card-title>
          <v-card-text>
            <v-container
              v-if="members && members.length === 0"
              class="d-flex justify-center align-center"
            >
              <img src="@/assets/box_empty.png" alt="Empty" />
              <p class="font-weight-medium">
                {{ $t('organization.details.empty') }}
              </p>
            </v-container>
            <v-data-table
              v-else
              :headers="users.headersUsers"
              :items="members"
              class="elevation-0 mt-5"
              :items-per-page="5"
              :footer-props="{
                'items-per-page-options': [5, 25, 50, 100, -1]
              }"
              :items-per-page-text="
                $t('organization.details.tabs.users.items_per_page')
              "
              :no-data-text="$t('organization.details.empty')"
              :no-results-text="
                $t('organization.details.tabs.users.empty_search')
              "
            >
              <template v-slot:item="{ item }">
                <tr>
                  <td>
                    {{ getUsername(item) }}
                  </td>
                  <td>
                    {{ item.email }}
                  </td>
                  <td>
                    {{
                      item && item.person && item.person.phone
                        ? item.person.phone
                        : '-'
                    }}
                  </td>
                  <td>
                    <v-icon
                      class="ml-2"
                      small
                      :color="item.status === true ? 'success' : 'danger'"
                    >
                      fiber_manual_record
                    </v-icon>
                  </td>
                  <td>
                    <v-menu>
                      <template v-slot:activator="{ on }">
                        <v-btn v-on="on" icon>
                          <v-icon>more_vert</v-icon>
                        </v-btn>
                      </template>
                      <v-list>
                        <v-list-item @click="editUser(item)">
                          <v-list-item-content>
                            <v-list-item-title>
                              {{ $t('general.edit') }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item @click="modalChange(item)">
                          <v-list-item-content>
                            <v-list-item-title>
                              {{ $t('general.change_password') }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item @click="openRemoveDialog(item.id)">
                          <v-list-item-content>
                            <v-list-item-title>
                              {{ $t('general.delete') }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- CAMPAIGNS -->
    <v-row no-gutters class="px-12 py-6">
      <v-col cols="12">
        <v-card class="rounded-lg" flat>
          <v-card-title
            class="subtitle-1 font-weight-medium black--text pt-4 ml-6"
          >
            {{ $t('organization.details.tabs.campaigns.title') }}
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-data-table
              :items="campaignsData.items"
              :single-expand="campaignsData.singleExpand"
              :expanded.sync="campaignsData.expanded"
              :server-items-length="campaignsData.total"
              :options="campaignsData.pagination"
              :no-data-text="$t('organization.details.empty')"
              :footer-props="{ 'items-per-page-options': [5, 15, 30, 50] }"
              :items-per-page="campaignsData.pagination.itemsPerPage"
              hide-default-header
              show-expand
              item-key="name"
              no-data-text="Empty"
              no-results-text="sin resultados"
              @update:options="campaignsData.pagination = $event"
            >
              <template v-slot:item="{ item, isExpanded, expand }">
                <tr @click="isExpanded ? expand(false) : expand(true)">
                  <td>
                    {{ item.name }}
                    <v-icon small :color="getStatusColor(item.status)">
                      fiber_manual_record
                    </v-icon>
                  </td>
                  <td class="text-right">
                    <v-icon>
                      {{
                        isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
                      }}
                    </v-icon>
                  </td>
                </tr>
              </template>
              <template v-slot:expanded-item="{ item }">
                <v-row
                  class="pa-4 d-flex justify-center align-center"
                  style="width: 100%;"
                >
                  <v-col cols="12" sm="6" md="3">
                    <p
                      class="subtitle-1 text-color font-weight-regular text-center"
                    >
                      {{ $t('subscriptions.dashboard.timestamp') }}
                    </p>
                    <p class="subtitle-1 font-weight-regular text-center">
                      {{ $moment(item.created_at).format('DD/MM/YYYY') }}
                    </p>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <p
                      class="subtitle-1 text-color font-weight-regular text-center"
                    >
                      {{
                        $t(
                          'organization.details.tabs.campaigns.total_transactions'
                        )
                      }}
                    </p>
                    <p class="subtitle-1 font-weight-regular text-center">
                      {{ item.totalTransactions ? item.totalTransactions : 0 }}
                    </p>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <p
                      class="subtitle-1 text-color font-weight-regular text-center"
                    >
                      {{ $t('organization.details.tabs.campaigns.goal') }}
                    </p>
                    <p class="subtitle-1 font-weight-regular text-center">
                      {{ item.amount_goal | valueFormatCurrency(currency) }}
                    </p>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <p
                      class="subtitle-1 text-color font-weight-regular text-center"
                    >
                      {{
                        $t('organization.details.tabs.campaigns.totalAcquired')
                      }}
                    </p>
                    <p class="subtitle-1 font-weight-regular text-center">
                      {{
                        item.totalAmountCollected
                          | valueFormatCurrency(currency)
                      }}
                    </p>
                  </v-col>
                </v-row>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- FORMS -->
    <v-row no-gutters class="px-12 py-6">
      <v-col cols="12">
        <v-card class="rounded-lg" flat>
          <v-card-title
            class="subtitle-1 font-weight-medium black--text pt-4 ml-6"
          >
            Formularios
          </v-card-title>
          <v-card-text>
            <v-tabs
              v-model="formsData.activeTab"
              centered
              grow
              slider-color="#2661FF"
            >
              <v-tab
                v-for="tab in formsData.tabs"
                :key="tab"
                class="font-weight-medium"
              >
                {{ tab }}
              </v-tab>
              <!-- LEADS TAB -->
              <v-tab-item>
                <v-data-table
                  :items="formsData.leads.items"
                  :single-expand="formsData.leads.singleExpand"
                  :expanded.sync="formsData.leads.expanded"
                  :server-items-length="formsData.leads.total"
                  :options="formsData.leads.pagination"
                  :no-data-text="$t('organization.details.empty')"
                  :footer-props="{ 'items-per-page-options': [5, 15, 30, 50] }"
                  :items-per-page="formsData.leads.pagination.itemsPerPage"
                  hide-default-header
                  show-expand
                  item-key="id"
                  no-data-text="Empty"
                  no-results-text="sin resultados"
                  @update:options="formsData.leads.pagination = $event"
                >
                  <template v-slot:item="{ item, isExpanded, expand }">
                    <tr @click="isExpanded ? expand(false) : expand(true)">
                      <td>
                        {{ item.name }}
                        <v-icon small :color="getStatusColor(item.status)">
                          fiber_manual_record
                        </v-icon>
                      </td>
                      <td class="text-right">
                        <v-icon>
                          {{
                            isExpanded
                              ? 'keyboard_arrow_up'
                              : 'keyboard_arrow_down'
                          }}
                        </v-icon>
                      </td>
                    </tr>
                  </template>
                  <template v-slot:expanded-item="{ item }">
                    <v-row
                      class="pa-4 d-flex justify-center align-center"
                      style="width: 100%;"
                    >
                      <v-col cols="12" sm="6" md="3">
                        <p
                          class="subtitle-1 text-color font-weight-regular text-center"
                        >
                          {{ $t('subscriptions.dashboard.timestamp') }}
                        </p>
                        <p class="subtitle-1 font-weight-regular text-center">
                          {{ $moment(item.created_at).format('DD/MM/YYYY') }}
                        </p>
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <p
                          class="subtitle-1 text-color font-weight-regular text-center"
                        >
                          {{
                            $t('organization.details.tabs.forms.interactions')
                          }}
                        </p>
                        <p class="subtitle-1 font-weight-regular text-center">
                          {{ item.views ? item.views : 0 }}
                        </p>
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <p
                          class="subtitle-1 text-color font-weight-regular text-center"
                        >
                          {{ $t('organization.details.tabs.forms.records') }}
                        </p>
                        <p class="subtitle-1 font-weight-regular text-center">
                          {{
                            item.registrations_count
                              ? item.registrations_count
                              : 0
                          }}
                        </p>
                      </v-col>
                    </v-row>
                  </template>
                </v-data-table>
              </v-tab-item>
              <!-- DONATIONS TAB -->
              <v-tab-item>
                <v-data-table
                  :items="formsData.donations.items"
                  :single-expand="formsData.donations.singleExpand"
                  :expanded.sync="formsData.donations.expanded"
                  :server-items-length="formsData.donations.total"
                  :options="formsData.donations.pagination"
                  :no-data-text="$t('organization.details.empty')"
                  :footer-props="{ 'items-per-page-options': [5, 15, 30, 50] }"
                  :items-per-page="formsData.donations.pagination.itemsPerPage"
                  hide-default-header
                  show-expand
                  item-key="id"
                  no-data-text="Empty"
                  no-results-text="sin resultados"
                  @update:options="formsData.donations.pagination = $event"
                >
                  <template v-slot:item="{ item, isExpanded, expand }">
                    <tr @click="isExpanded ? expand(false) : expand(true)">
                      <td>
                        {{ item.name }}
                        <v-icon small :color="getStatusColor(item.status)">
                          fiber_manual_record
                        </v-icon>
                      </td>
                      <td class="text-right">
                        <v-icon>
                          {{
                            isExpanded
                              ? 'keyboard_arrow_up'
                              : 'keyboard_arrow_down'
                          }}
                        </v-icon>
                      </td>
                    </tr>
                  </template>
                  <template v-slot:expanded-item="{ item }">
                    <v-row
                      class="pa-4 d-flex justify-center align-center"
                      style="width: 100%;"
                    >
                      <v-col cols="12" sm="6" md="3">
                        <p
                          class="subtitle-1 text-color font-weight-regular text-center"
                        >
                          {{ $t('subscriptions.dashboard.timestamp') }}
                        </p>
                        <p class="subtitle-1 font-weight-regular text-center">
                          {{ $moment(item.created_at).format('DD/MM/YYYY') }}
                        </p>
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <p
                          class="subtitle-1 text-color font-weight-regular text-center"
                        >
                          {{
                            $t(
                              'organization.details.tabs.forms.donations.total_transactions'
                            )
                          }}
                        </p>
                        <p class="subtitle-1 font-weight-regular text-center">
                          {{
                            item.totalTransactions
                              | valueFormatCurrency(currency)
                          }}
                        </p>
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <p
                          class="subtitle-1 text-color font-weight-regular text-center"
                        >
                          {{
                            $t(
                              'organization.details.tabs.forms.donations.total_subscriptions'
                            )
                          }}
                        </p>
                        <p class="subtitle-1 font-weight-regular text-center">
                          {{
                            item.totalSubscriptions
                              ? item.totalSubscriptions
                              : 0
                          }}
                        </p>
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <p
                          class="subtitle-1 text-color font-weight-regular text-center"
                        >
                          {{
                            $t(
                              'organization.details.tabs.forms.donations.totalAcquired'
                            )
                          }}
                        </p>
                        <p class="subtitle-1 font-weight-regular text-center">
                          {{
                            item.totalAmountCollected
                              | valueFormatCurrency(currency)
                          }}
                        </p>
                      </v-col>
                    </v-row>
                  </template>
                </v-data-table>
              </v-tab-item>
            </v-tabs>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- OBSERVATIONS -->
    <ObservationsDialog v-if="isObservationsDialogOpen" />

    <!-- QUICKBOOKS -->
    <QuickbooksDialog v-if="isQuickbooksDialogOpen" />

    <!-- BILLING -->
    <v-navigation-drawer
      v-if="
        organization.organization_plan &&
          ![getPlanIdByName('no_plan')].includes(
            organization.organization_plan.plan_id
          )
      "
      :value="openBillingDrawer"
      @input="inputBillingDrawer"
      :width="'calc(100vw - 55%)'"
      temporary
      fixed
      right
      color="grey lighten-3"
      style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1); padding-top: 65px;"
    >
      <BillingDrawer @close="openBillingDrawer = false" />
    </v-navigation-drawer>
    <v-navigation-drawer
      v-if="
        organization.organization_plan &&
          ![getPlanIdByName('no_plan')].includes(
            organization.organization_plan.plan_id
          )
      "
      :value="showHistoric"
      @input="inputHistoricDrawer"
      :width="'90%'"
      temporary
      fixed
      right
      color="grey lighten-3"
      style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1); padding-top: 65px;"
    >
      <BillingHistory
        :organization_id="organization.id"
        @close="showHistoric = false"
      />
    </v-navigation-drawer>

    <!-- ACCOUNT DETAILS -->
    <v-navigation-drawer
      v-if="
        organization.organization_plan &&
          ![getPlanIdByName('no_plan')].includes(
            organization.organization_plan.plan_id
          )
      "
      :value="openAccountDetails"
      @input="inputAccountDetails"
      :width="'calc(100vw - 55%)'"
      temporary
      fixed
      right
      color="grey lighten-3"
      style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1); padding-top: 65px;"
    >
      <AccountDetails @close="openAccountDetails = false" />
    </v-navigation-drawer>

    <!-- PLANS -->
    <v-navigation-drawer
      :value="isOrganizationPlanDetailsOpen"
      @input="inputOrganizationPlanDetails"
      :width="'calc(100vw - 55%)'"
      temporary
      fixed
      right
      color="grey lighten-3"
      style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1); padding-top: 65px;"
    >
      <PlanDrawer
        v-if="isOrganizationPlanDetailsOpen"
        :organizationPlan="organizationPlan"
        :organization_id="organization.id"
        @close="closeOrganizationPlanDetail()"
      />
    </v-navigation-drawer>

    <!-- RESET USER PASSWORD -->
    <ChangeUserPassDialog
      :dialog="users.dialog.value"
      :user="users.dialog.user"
      @save="changePassword"
      @close="closeDialog"
    />
    <!-- REMOVE USER -->
    <BaseConfirmDialog
      v-if="users.dialogRemove"
      :title="$t('organization.users.delete.title')"
      :description="$t('organization.users.delete.description')"
      :accept-text="$t('general.confirm')"
      :cancel-text="$t('general.cancel')"
      @close="closeRemoveDialog()"
      @proceed="confirmRemoveDialog()"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import organizationMixin from '@/mixins/organization'
import InfoRow from '@/components/Organization/Details/InfoRow'
import DateFilters from '@/components/Common/DateFilters/Index'
import DateBar from '@/components/Common/DateBar/Index'
import LineChart from '@/components/Charts/Line'
import ChangeUserPassDialog from '@/components/Organization/Details/ChangeUserPassDialog'
import BaseConfirmDialog from '@/components/BaseConfirmDialog'
import PlanDrawer from '@/components/Organization/Details/PlanDrawer'
import BillingDrawer from '@/components/Billing/BillingDrawer'
import BillingHistory from '@/components/Billing/BillingHistory'
import AccountDetails from '@/components/Organization/Details/AccountDetails'
import ObservationsDialog from '@/components/Organization/Details/ObservationsDialog'
import plansMixin from '@/mixins/plans'
import QuickbooksDialog from './QuickbooksDialog.vue'

export default {
  name: 'organizationDashboard',
  mixins: [organizationMixin, plansMixin],
  components: {
    InfoRow,
    DateFilters,
    DateBar,
    LineChart,
    ChangeUserPassDialog,
    BaseConfirmDialog,
    PlanDrawer,
    BillingDrawer,
    BillingHistory,
    AccountDetails,
    ObservationsDialog,
    QuickbooksDialog
  },
  data() {
    return {
      isLoading: false,
      // Drawers
      openBillingDrawer: false,
      showHistoric: false,
      openAccountDetails: false,
      // OrganizationDetails
      filterOrganizationStatus: [],
      // Sections
      dashboard: {
        dateRange: 3,
        startDate: this.$moment()
          .startOf('month')
          .startOf('day')
          .format('YYYY-MM-DD'),
        endDate: this.$moment().format('YYYY-MM-DD'),
        datacollection: {}
      },
      users: {
        dialog: {
          value: false,
          user: {}
        },
        idUserRemove: null,
        dialogRemove: false,
        headersUsers: [
          {
            text: this.$t('organization.details.tabs.users.name'),
            value: 'person.first_name',
            class: 'text-color'
          },
          {
            text: this.$t('organization.details.tabs.users.email'),
            value: 'email',
            class: 'text-color'
          },
          {
            text: this.$t('organization.details.tabs.users.phone'),
            value: 'person.phone',
            class: 'text-color'
          },
          {
            text: this.$t('organization.details.tabs.users.status'),
            value: 'status',
            sortable: false,
            class: 'text-color'
          },
          {
            text: '',
            value: 'option',
            sortable: false
          }
        ]
      },
      campaignsData: {
        expanded: [],
        singleExpand: true,
        items: [],
        total: 0,
        pagination: {
          page: 1,
          itemsPerPage: 5
        }
      },
      formsData: {
        activeTab: null,
        tabs: [
          this.$t('organization.details.tabs.forms.lead.title'),
          this.$t('organization.details.tabs.forms.donations.title')
        ],
        leads: {
          expanded: [],
          singleExpand: true,
          items: [],
          total: 0,
          pagination: {
            page: 1,
            itemsPerPage: 5
          }
        },
        donations: {
          expanded: [],
          singleExpand: true,
          items: [],
          total: 0,
          pagination: {
            page: 1,
            itemsPerPage: 5
          }
        }
      }
    }
  },
  async created() {
    try {
      this.isLoading = true
      this.initializeFilterOrganizationStatus()
      // Dashboard
      await this.changeDates()
      // Users
      await this.getUsers()
      // Campaigns
      await this.getCampaigns()
      // Forms
      await this.getLeadForms()
      await this.getDonationForms()
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
    }
  },
  watch: {
    startDate(newValue, oldValue) {
      if (newValue > this.endDate) {
        this.$noty.error(this.$t('general.error.invalid_range'))
        this.startDate = oldValue
      }
    },
    endDate(newValue, oldValue) {
      if (newValue < this.startDate) {
        this.$noty.error(this.$t('general.error.invalid_range'))
        this.endDate = oldValue
      }
    },
    // Campaigns
    'campaignsData.pagination': {
      async handler() {
        await this.getCampaigns()
      }
    },
    // Forms
    'formsData.leads.pagination': {
      async handler() {
        await this.getLeadForms()
      }
    },
    'formsData.donations.pagination': {
      async handler() {
        await this.getDonationForms()
      }
    }
  },
  computed: {
    ...mapState({
      organization: ({ organization: { organization } }) => organization,
      orgId: ({ organization: { orgId } }) => orgId,
      currency: ({ organization: { organization } }) =>
        organization.currency.symbol,
      organizationPlan: ({ organization: { organizationPlan } }) =>
        organizationPlan,
      isOrganizationPlanDetailsOpen: ({
        organization: { isOrganizationPlanDetailsOpen }
      }) => isOrganizationPlanDetailsOpen,
      members: ({ organization: { members } }) => members,
      campaigns: ({ organization: { campaigns } }) => campaigns,
      totalCampaigns: ({ organization: { totalCampaigns } }) => totalCampaigns,
      formsLeads: ({ organization: { formsLeads } }) => formsLeads,
      formsLeadsTotal: ({ organization: { formsLeadsTotal } }) =>
        formsLeadsTotal,
      formsDonations: ({ organization: { formsDonations } }) => formsDonations,
      formsDonationsTotal: ({ organization: { formsDonationsTotal } }) =>
        formsDonationsTotal,
      organizationPlan: ({ organization: { organizationPlan } }) =>
        organizationPlan,
      isObservationsDialogOpen: ({
        organization: { isObservationsDialogOpen }
      }) => isObservationsDialogOpen,
      isQuickbooksDialogOpen: ({ organization: { isQuickbooksDialogOpen } }) =>
        isQuickbooksDialogOpen
    }),
    showOrganizationHeaderActions() {
      const {
        filterOrganizationStatus,
        organization,
        getStatusObject,
        getPlanIdByName
      } = this

      const {
        status: organizationStatus,
        organization_plan: organizationPlan
      } = organization

      // Validations
      const isStatusValid = !filterOrganizationStatus.includes(
        getStatusObject(organizationStatus).value
      )
      const hasOrganizationPlan =
        !!organizationPlan && Object.keys(organizationPlan).length

      if (!hasOrganizationPlan) return false

      const isPlanValid = ![getPlanIdByName('no_plan')].includes(
        organizationPlan.plan_id
      )

      return isStatusValid && hasOrganizationPlan && isPlanValid
    }
  },
  methods: {
    ...mapActions({
      openOrganizationPlanDetail: 'organization/openOrganizationPlanDetail',
      getDataChart: 'organization/getDataChart',
      getOrganizationMembers: 'organization/getOrganizationMembers',
      updateUserPassword: 'organization/updateUserPassword',
      removeUser: 'organization/removeUser',
      getOrganizationCampaigns: 'organization/getOrganizationCampaigns',
      getOrganizationForms: 'organization/getOrganizationForms'
    }),
    ...mapMutations({
      closeOrganizationPlanDetail: 'organization/closeOrganizationPlanDetail',
      changeOrganizationUser: 'organization/changeOrganizationUser',
      organizationDrawerUser: 'organization/organizationDrawerUser',
      toggleObservationsDialog: 'organization/toggleObservationsDialog',
      toggleQuickbooksDialog: 'organization/toggleQuickbooksDialog'
    }),
    initializeFilterOrganizationStatus() {
      this.filterOrganizationStatus = [
        this.getStatusObject('canceled').value,
        this.getStatusObject('deleted').value,
        this.getStatusObject('suspended').value
      ]
    },
    /* ORGANIZATION SECTION */
    async openPlan(organization_id) {
      await this.openOrganizationPlanDetail({ organization_id })
    },
    inputOrganizationPlanDetails(arg) {
      if (!arg) {
        this.closeOrganizationPlanDetail()
      }
    },
    inputBillingDrawer(arg) {
      if (!arg) {
        this.openBillingDrawer = false
      }
    },
    inputHistoricDrawer(arg) {
      if (!arg) {
        this.showHistoric = false
      }
    },
    inputAccountDetails(arg) {
      if (!arg) {
        this.openAccountDetails = false
      }
    },
    updateDates(property, date) {
      this[property] = date
      const startDate = this.$moment(this.dashboard.startDate).unix()
      const endDate = this.$moment(this.dashboard.endDate)
        .endOf('day')
        .unix()
      this.changeDates('customized', startDate, endDate)
    },
    async changeDates(code, start, end = null) {
      const startDate = !start
        ? this.$moment(this.dashboard.startDate).unix()
        : start
      const endDate = !end
        ? this.$moment(this.dashboard.endDate)
            .endOf('day')
            .unix()
        : end
      if (code === 'customized') {
        this.dashboard.dateRange = 7
        await this.getDashboardData(startDate, endDate)
      } else {
        await this.getDashboardData(startDate, endDate)
      }
    },
    openObservationsDialog() {
      this.toggleObservationsDialog(true)
    },
    openQuickbooksDialog() {
      this.toggleQuickbooksDialog(true)
    },
    /* DASHBOARD SECTION */
    async getDashboardData(
      start,
      end = this.$moment()
        .endOf('day')
        .unix()
    ) {
      try {
        const { timeLineChart } = (
          await this.getDataChart({ id: this.orgId, start, end })
        ).data
        this.dashboard.datacollection = {
          labels: timeLineChart.totalLeads.map(({ serie }) => serie),
          datasets: [
            {
              label: this.$t('dashboard.conversions_leads'),
              data: timeLineChart.totalLeads.map(({ total }) => total),
              backgroundColor: 'rgb(255, 145, 104)',
              borderColor: 'rgb(255, 145, 104)',
              fill: false,
              yAxisID: 'totalLeads'
            },
            {
              label: this.$t('dashboard.oneTime'),
              data: timeLineChart.totalOnce.map(({ total }) => total),
              backgroundColor: 'rgb(255, 233, 32)',
              borderColor: 'rgb(255, 233, 32)',
              fill: false,
              yAxisID: 'totalLeads'
            },
            {
              label: this.$t('dashboard.recurrency'),
              data: timeLineChart.totalRecurrent.map(({ total }) => total),
              backgroundColor: 'rgb(88, 168, 246)',
              borderColor: 'rgb(88, 168, 246)',
              fill: false,
              yAxisID: 'totalLeads'
            },
            {
              label: this.$t('dashboard.total_acquired'),
              data: timeLineChart.totalDonations.map(({ total }) => total),
              backgroundColor: '#F66F06',
              borderColor: '#F66F06',
              fill: false,
              yAxisID: 'totalDonations'
            }
          ]
        }

        this.dashboard.startDate = this.$moment.unix(start).format('YYYY-MM-DD')
        this.dashboard.endDate = this.$moment.unix(end).format('YYYY-MM-DD')
      } catch (error) {
        this.$noty.error(this.$t('dashboard.error'))
      }
    },
    isOrganizationDeleted() {
      return (
        this.filterOrganizationStatus.includes(
          this.getStatusObject(this.organization.status).value
        ) && this.organization.restriction_date
      )
    },
    /* USERS SECTION */
    async getUsers() {
      try {
        await this.getOrganizationMembers(this.orgId)
      } catch (error) {
        this.$noty.error(this.$t('dashboard.error'))
      }
    },
    create() {
      this.organizationDrawerUser({ type: 'new', value: true })
    },
    editUser(data) {
      this.changeOrganizationUser(data)
      this.organizationDrawerUser({ type: 'edit', value: true })
    },
    getUsername(data) {
      if (data && data.person) {
        return `${data.person.first_name} ${data.person.last_name}`
      }
      return `-`
    },
    modalChange(data) {
      this.users.dialog = {
        value: true,
        user: {
          id: data.id,
          name: `${data.person.first_name} ${data.person.last_name}`,
          email: data.email
        }
      }
    },
    changePassword(data) {
      this.updateUserPassword({
        id: this.orgId,
        uid: data.id,
        body: {
          email: data.email,
          password: data.password,
          password_confirmation: data.password
        }
      }).then(
        res => {
          this.$noty.success(this.$t('organization.users.success_update'))
          this.closeDialog()
        },
        err => {
          this.$noty.error(this.$t('organization.users.error_update'))
        }
      )
    },
    closeDialog() {
      this.users.dialog = {
        value: false,
        user: {}
      }
    },
    openRemoveDialog(id) {
      this.users.idUserRemove = id
      this.users.dialogRemove = true
    },
    closeRemoveDialog() {
      this.users.idUserRemove = null
      this.users.dialogRemove = false
    },
    async confirmRemoveDialog() {
      try {
        const res = (
          await this.removeUser({
            organizationId: this.orgId,
            id: this.users.idUserRemove
          })
        ).data
        if (res.success) {
          this.$noty.success(
            this.$t('organization.users.delete.message.success')
          )
        } else {
          this.$noty.error(this.$t('organization.users.delete.message.error'))
        }
      } catch (error) {
        if (error.response && error.response.data) {
          if (error.response.data.message === 'user_is_owner') {
            this.$noty.error(
              this.$t('organization.users.delete.message.error_owner')
            )
          } else {
            this.$noty.error(this.$t('organization.users.delete.message.error'))
          }
        } else {
          this.$noty.error(this.$t('organization.users.delete.message.error'))
        }
      } finally {
        this.closeRemoveDialog()
      }
    },
    /* CAMPAIGNS SECTION */
    async getCampaigns() {
      try {
        const { page, itemsPerPage } = this.campaignsData.pagination
        const id = this.orgId
        await this.getOrganizationCampaigns({ id, page, itemsPerPage })
        this.campaignsData.total = Number(this.totalCampaigns)
        this.campaignsData.items = this.campaigns
      } catch (error) {
        this.$noty.error(this.$t('dashboard.error'))
      }
    },
    getStatusColor(status) {
      if (status === 1) {
        return 'success'
      }
      if (status === 2) {
        return 'red'
      }
      if (status === 3) {
        return 'dark'
      }
    },
    /* FORMS SECTION */
    async getLeadForms() {
      try {
        const { page, itemsPerPage } = this.formsData.leads.pagination
        const [id, commit, type] = [this.orgId, 'setOrgFormsLeads', 1]
        await this.getOrganizationForms({
          id,
          type,
          page,
          itemsPerPage,
          commit
        })

        this.formsData.leads.items = this.formsLeads
        this.formsData.leads.total = Number(this.formsLeadsTotal)
      } catch (error) {
        this.$noty.error(this.$t('dashboard.error'))
      }
    },
    async getDonationForms() {
      try {
        const { page, itemsPerPage } = this.formsData.donations.pagination
        const [id, commit, type] = [this.orgId, 'setOrgFormsDonations', 2]
        await this.getOrganizationForms({
          id,
          type,
          page,
          itemsPerPage,
          commit
        })

        this.formsData.donations.items = this.formsDonations
        this.formsData.donations.total = Number(this.formsDonationsTotal)
      } catch (error) {
        this.$noty.error(this.$t('dashboard.error'))
      }
    }
  }
}
</script>

<style lang="scss">
.text-color {
  color: #2661ff !important;
}
.button-container {
  display: flex;
  justify-content: start; /* Distribute items evenly */
  align-items: center; /* Vertically center items */
}

.responsive-button {
  display: flex; /* Initially hide all buttons */

  /* Show buttons for screens with a max-width of 600px */
  @media (max-width: 768px) {
    display: none;
  }
}

@media screen and (max-width: 768px) {
  .button-container {
    flex-direction: column; /* Stack items vertically on smaller screens */
    align-items: flex-start; /* Align items to the left on smaller screens */
  }
}
</style>
